import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { Box, Button, Stack, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import {
	getUseInvoiceQueryKey,
	getUseTransactionsForInvoiceQueryKey,
	useInvoice,
	usePayInvoiceMutation,
	useReverseInvoiceMutation,
	useTransactionsForInvoice,
	useWriteOffInvoiceMutation,
} from '../api/InvoiceApi';
import { getUsePolicyQueryKey } from '../api/PolicyApi';
import {
	AuthContext,
	AuthData,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
} from '../context/auth';
import { ConfigContext, ConfigData } from '../context/config';
import { CurrencyCode } from '../types/currencyCode';
import { Payment } from '../types/payments';
import {
	PaymentCreateRequest,
	PaymentReversalReason,
	PolicyInvoice2Response,
	PolicyInvoice2StatusAndTime,
} from '../types/policy';
import { getTransactionSummary } from '../util/util';
import { StyledEmptyMessage } from './common';
import DropdownButton, { HiddenActionItem } from './DropdownButton';
import InvoiceSummary from './InvoiceSummary';
import LargeCenterSpinner from './LargeCenterSpinner';
import PayInvoiceModal from './PayInvoiceModal';
import PaymentAccordions from './PaymentAccordions';
import NewPayInvoiceModal from './payments/components/PayInvoiceModal';
import NewReversePaymentModal from './payments/components/ReversePaymentModal';
import WriteOffPaymentModal from './payments/components/WriteOffPaymentModal';
import {
	POLICY_INVOICE_VIEW__COPY_INVOICE_BUTTON,
	POLICY_INVOICE_VIEW__COPY_INVOICE_SUCCESS_TOAST,
	POLICY_INVOICE_VIEW__DOWNLOAD_INVOICE_BUTTON,
	POLICY_INVOICE_VIEW__REVERSE_PAYMENT_BUTTON_TEXT,
	POLICY__INVOICE_VIEW__PAY_INVOICE_BUTTON,
	POLICY__INVOICE_VIEW__TITLE,
	POLICY__INVOICE_VIEW__WRITE_OFF_BUTTON,
} from './policy/data/constants';
import ReversePaymentModal from './ReversePaymentModal';
import { infoToast } from './Toast';
import TransactionsDetailTable from './TransactionsDetailTable';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	invoice: PolicyInvoice2Response;
	onPayment: (
		policyId: string,
		locator: string,
		req: PaymentCreateRequest,
	) => void;
	onReverse: (locator: string) => void;
	onWriteOff: (locator: string) => void;
	callback: () => void;
	currency: CurrencyCode;
}

interface InvoiceModalProps {
	open: boolean;
	invoice?: PolicyInvoice2Response;
}

const PaymentsList = (props: Props) => {
	const queryClient = useQueryClient();
	const {
		authData,
		invoice: invoiceFromProps,
		onPayment,
		onReverse,
		callback,
		currency,
	} = props;
	const { hasPermission } = authData;
	const authToken = authData.auth.authorizationToken;
	const [payInvoiceModalInfo, setPayInvoiceModalInfo] =
		React.useState<InvoiceModalProps>({ invoice: null, open: false });
	const [reversePaymentModalOpen, setReversePaymentModalOpen] =
		React.useState<InvoiceModalProps>({ open: false, invoice: null });
	const [writeOffInvoiceModal, setWriteOffInvoiceModal] =
		React.useState<InvoiceModalProps>({ open: false, invoice: null });
	const writeOffInvoiceMutation = useWriteOffInvoiceMutation(
		authToken,
		queryClient,
	);
	const payInvoiceMutation = usePayInvoiceMutation(authToken, queryClient);
	const reverseInvoiceMutation = useReverseInvoiceMutation(
		authToken,
		queryClient,
	);

	const { data: invoiceResponse, isLoading: isInvoiceLoading } = useInvoice(
		authToken,
		invoiceFromProps?.locator,
	);

	const { data: transactionsResponse, isLoading: isTransactionsLoading } =
		useTransactionsForInvoice(authToken, invoiceFromProps?.locator);

	const transactions = transactionsResponse?.data?.financialTransactions || [];
	const invoice = invoiceResponse?.data;
	const isPremiumReportingInvoice = invoice?.invoiceType === 'premiumReporting';
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const [expanded, setExpanded] = useState([] as string[]);
	const isLoading = isInvoiceLoading || isTransactionsLoading;

	if (!invoice && isLoading) {
		return <LargeCenterSpinner />;
	}

	const refetchData = (policyId?: string) => {
		const policyQueryKey = getUsePolicyQueryKey(policyId, authToken);
		const invoiceQueryKey = getUseInvoiceQueryKey(
			authToken,
			payInvoiceModalInfo?.invoice?.locator ||
				reversePaymentModalOpen.invoice?.locator ||
				'',
		);
		const transactionsQueryKey = getUseTransactionsForInvoiceQueryKey(
			authToken,
			invoice.locator,
		);

		queryClient.invalidateQueries(invoiceQueryKey);
		queryClient.invalidateQueries(transactionsQueryKey);
		queryClient.invalidateQueries(policyQueryKey);
		// callback to fetch entity specific data after the invoice transaction
		if (callback) {
			callback();
		}
	};

	const reversePayment = async (
		paymentLocator: string,
		policyLocator: string,
		reversalReason: PaymentReversalReason,
	) => {
		await reverseInvoiceMutation.mutateAsync({
			paymentLocator,
			reversalReason,
		});
		refetchData(policyLocator);
	};

	const writeOffInvoice = async (
		invoiceLocator: string,
		policyLocator: string,
	) => {
		await writeOffInvoiceMutation.mutateAsync({ invoiceLocator });
		refetchData(policyLocator);
	};

	const payInvoice = async (
		policyId: string,
		locator: string,
		paymentCreateReq: any,
	): Promise<Payment> => {
		const response = await payInvoiceMutation.mutateAsync({
			paymentCreateReq,
			invoiceLocator: locator,
		});
		refetchData(policyId);
		return response.data;
	};

	const recordPayment = async (
		policyId: string,
		locator: string,
		req: PaymentCreateRequest,
	) => {
		await onPayment(policyId, locator, req);
		refetchData(policyId);
	};

	const handleChange = (panel: any) => (event: any, isExpanded: boolean) => {
		const expandedAccordions = [...expanded];
		if (isExpanded) {
			expandedAccordions.push(panel);
		} else {
			const i = expanded.indexOf(panel);
			expandedAccordions.splice(i, 1);
		}
		setExpanded(expandedAccordions);
	};

	const renderTransactions = () => {
		const transactionSummary = (
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					width: '80%',
				}}
			>
				{getTransactionSummary(transactions, invoice, currency).map(
					(total, index) => {
						const label = (
							<Box component='span' fontWeight='500'>
								{total.label}
							</Box>
						);
						return (
							<div key={total.label + index} style={{ display: 'flex' }}>
								<Typography sx={{ mr: 1 }}>
									{label}
									{total.value}
								</Typography>{' '}
							</div>
						);
					},
				)}
			</div>
		);

		return transactions.length > 0 ? (
			<Accordion
				key={invoice.locator}
				expanded={expanded.includes(invoice.locator)}
				onChange={handleChange(invoice.locator)}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} id={invoice.locator}>
					{transactionSummary}
				</AccordionSummary>
				<AccordionDetails style={{ display: 'flex', flexDirection: 'column' }}>
					<TransactionsDetailTable
						transactions={transactions}
						currency={currency}
						policyLocator={invoice.policyLocator}
					/>
				</AccordionDetails>
			</Accordion>
		) : null;
	};

	const payInvoiceButton = () => {
		const invoiceStatus = invoice.settlementStatus;
		if (invoiceStatus !== 'settled' && hasPermission('invoice.pay')) {
			return (
				<PayInvoiceModal
					invoiceLocator={invoice.locator}
					isPaid={invoice.settlementStatus === 'settled'}
					onPayment={recordPayment}
					currency={currency}
				/>
			);
		} else if (
			invoiceStatus === 'settled' &&
			invoice.settlementType === 'paid' &&
			hasPermission('payment.reverse')
		) {
			const latestPayment = invoice.payments.find(
				(p: Payment) => !p.reversedTimestamp,
			);
			if (!latestPayment) {
				return null;
			}
			return (
				<ReversePaymentModal
					paymentLocator={latestPayment.locator}
					displayId={latestPayment.displayId}
					onReverse={onReverse}
				/>
			);
		} else {
			return null;
		}
	};

	const actions: HiddenActionItem[] = [];

	if (
		invoice.settlementStatus === 'settled' &&
		invoice.settlementType === 'paid' &&
		hasPermission('payment.reverse') &&
		cashManagementEnabled &&
		!isPremiumReportingInvoice
	) {
		const latestPayment = invoice.payments.find(
			(p: Payment) => !p.reversedTimestamp,
		);
		if (latestPayment) {
			actions.push({
				label: POLICY_INVOICE_VIEW__REVERSE_PAYMENT_BUTTON_TEXT,
				icon: <HistoryIcon />,
				callback: () => {
					setReversePaymentModalOpen({ open: true, invoice });
				},
			});
		}
	}

	const writtenOff = invoice.statuses.some(
		(status: PolicyInvoice2StatusAndTime) => status.status === 'writtenOff',
	);
	const outstanding = invoice.settlementStatus === 'outstanding';

	if (hasPermission('invoice.writeOff') && !writtenOff && outstanding) {
		actions.push({
			label: POLICY__INVOICE_VIEW__WRITE_OFF_BUTTON,
			icon: <MoneyOffIcon />,
			callback: () => {
				setWriteOffInvoiceModal({ open: true, invoice });
			},
		});
	}

	if (invoice.documents[0]?.url) {
		actions.push({
			label: POLICY_INVOICE_VIEW__DOWNLOAD_INVOICE_BUTTON,
			icon: <DownloadIcon />,
			callback: () => {
				window.open(invoice.documents[0].url, '_blank');
			},
		});
	}

	actions.push({
		label: POLICY_INVOICE_VIEW__COPY_INVOICE_BUTTON,
		icon: <ContentCopyIcon />,
		callback: () => {
			navigator.clipboard.writeText(invoice.locator);
			infoToast(POLICY_INVOICE_VIEW__COPY_INVOICE_SUCCESS_TOAST);
		},
	});

	const noPaymentsMessage = () => {
		return <StyledEmptyMessage>No Payments</StyledEmptyMessage>;
	};

	return (
		<>
			<Stack
				spacing={2}
				direction='row'
				justifyContent='space-between'
				mb={2}
				mt={2}
			>
				<Stack
					spacing={2}
					direction='row'
					justifyContent='space-between'
					alignItems='center'
				>
					<Typography variant='h3'>
						{POLICY__INVOICE_VIEW__TITLE + ' ' + invoice.displayId}
					</Typography>
					{cashManagementEnabled &&
						!isPremiumReportingInvoice &&
						invoice.settlementStatus === 'outstanding' &&
						hasPermission('invoice.pay') && (
							<Button
								variant='contained'
								onClick={() => setPayInvoiceModalInfo({ open: true, invoice })}
							>
								{POLICY__INVOICE_VIEW__PAY_INVOICE_BUTTON}
							</Button>
						)}
					{(!cashManagementEnabled || isPremiumReportingInvoice) &&
						payInvoiceButton()}
				</Stack>
				<DropdownButton actions={actions} />
			</Stack>
			<InvoiceSummary invoice={invoice} authData={authData} />
			{renderTransactions()}
			{invoice.payments?.length ? (
				<PaymentAccordions payments={invoice.payments} />
			) : (
				noPaymentsMessage()
			)}
			{payInvoiceModalInfo?.invoice && (
				<NewPayInvoiceModal
					callback={payInvoice}
					isCallbackLoading={payInvoiceMutation.isLoading}
					isCallbackSuccess={payInvoiceMutation.isSuccess}
					open={payInvoiceModalInfo.open}
					setOpen={() => {
						setPayInvoiceModalInfo({
							open: false,
						});
					}}
					invoice={payInvoiceModalInfo.invoice}
				/>
			)}
			{reversePaymentModalOpen?.invoice && (
				<NewReversePaymentModal
					callback={reversePayment}
					open={reversePaymentModalOpen.open}
					handleClose={() => {
						setReversePaymentModalOpen({
							open: false,
							invoice: null,
						});
					}}
					invoice={reversePaymentModalOpen.invoice}
				/>
			)}
			{writeOffInvoiceModal?.invoice && (
				<WriteOffPaymentModal
					callback={writeOffInvoice}
					open={writeOffInvoiceModal.open}
					handleClose={() => {
						setWriteOffInvoiceModal({
							open: false,
							invoice: null,
						});
					}}
					invoice={writeOffInvoiceModal.invoice}
				/>
			)}
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} authData={authData} configData={configData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(PaymentsList);
